'use client';

import { Spinner } from '@phosphor-icons/react';
import { DropRippleLoader } from '@/components/visualizations/drop-ripple-loader';
import { cn } from '@/lib/utils';
import { getStreamingSvgPreview, resolveSvgBackgroundColor } from '@/lib/svg-streaming';
import { useThrottledValue } from '@/lib/use-throttled-value';

interface StreamingSvgPreviewProps {
  input?: Record<string, unknown>;
  className?: string;
}

// Match the html-preview throttle — same goal: stop wiping/redrawing the
// inner SVG on every chunk. innerHTML resets here don't reload an iframe
// but they do destroy/recreate the entire SVG subtree, which can flicker
// for complex visuals during fast token bursts.
const MARKUP_THROTTLE_MS = 700;

export function StreamingSvgPreview({ input, className }: StreamingSvgPreviewProps) {
  const preview = getStreamingSvgPreview(input);

  // Compute markup unconditionally so the hook below has a stable call order
  // regardless of whether `preview` is null. Empty string is fine — the
  // null branch returns the loader card before we render markup anyway.
  const bgColor = preview ? resolveSvgBackgroundColor(preview.background) : '';
  const constrainedStyle = preview?.maxHeight ? `max-height: ${preview.maxHeight}px; overflow: auto;` : '';
  const markup = preview
    ? `
    <style>
      .svg-streaming-shell {
        width: 100%;
        ${constrainedStyle}
      }

      .svg-streaming-shell svg {
        display: block;
        width: 100%;
        height: auto;
        max-width: 100%;
      }

      ${preview.css || ''}
    </style>
    <div class="svg-streaming-shell">${preview.svg}</div>
  `.trim()
    : '';
  const stableMarkup = useThrottledValue(markup, MARKUP_THROTTLE_MS);

  if (!preview) {
    return (
      <div className={cn('flex flex-col rounded-xl border border-border bg-muted/20', className)}>
        <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
          <Spinner className="size-3.5 animate-spin" weight="bold" />
          <span>Streaming SVG preview</span>
        </div>
        <div className="flex flex-1 flex-col overflow-hidden rounded-b-xl" style={{ minHeight: 260 }}>
          <DropRippleLoader label="Drafting vectors…" />
        </div>
      </div>
    );
  }

  return (
    <div className={cn('rounded-xl border border-border bg-muted/20', className)}>
      <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
        <Spinner className="size-3.5 animate-spin" weight="bold" />
        <span>Streaming SVG preview</span>
        {preview.title ? (
          <span className="min-w-0 truncate text-foreground/80">· {preview.title}</span>
        ) : null}
      </div>
      <div className="px-3 pb-3">
        <div
          className="overflow-auto rounded-md border border-border/60 p-2"
          style={{ backgroundColor: bgColor, minHeight: preview.minHeight, maxHeight: preview.maxHeight }}
        >
          <div
            className="w-full"
            dangerouslySetInnerHTML={{ __html: stableMarkup }}
          />
        </div>
      </div>
    </div>
  );
}
